import { acceptHMRUpdate, defineStore } from "pinia";
import { fetchOneEntry } from "@builder.io/sdk-vue";
import type { APIState } from "~/types/api-state.type";

export const useBuilderStore = defineStore("builderStore", {
  state: () => ({
    content: null,
    content_path: {},
    locales: null,
    builder_api_key: useRuntimeConfig().public.builderApiKey,
    builder_api_pvt_key: useRuntimeConfig().public.builderApiPvtKey, // Private key is used for updating interested count in event detail page. Should not be public
    list: [] as Array<any>,
    item: {} as object,
    currentDate: new Date().getTime(),
    query: {} as object,
    month_array: [] as Array<string>,
    blogItem: null as any,
    blogs: [] as Array<any>,
    news: [] as Array<any>,
    adviceItem: null as any,
    advices: [] as Array<any>,
    error_messages: [] as Array<any>,
    interested_count: 0,
    is_interested: false,
    blogsCategories: [] as Array<any>,
    topBrands: [] as Array<any>,
    brands: [] as Array<any>,
    filteredEvents: [] as Array<any>, 
    brands_state: <APIState>{
      status: "idle",
      data: {},
      message: "",
    },
    brand_list_state: <APIState>{
      status: "idle",
      data: {},
      message: "",
    },

    blog_type_list: [
      {
        name: "Blogs",
        slug: "blogs",
      },
      {
        name: "News",
        slug: "news",
      },
      {
        name: "Advices",
        slug: "advices",
      },
      {
        name: "All",
        slug: "all",
      },
    ],
    event_state: <APIState>{
      status: "idle",
      data: {},
      message: "",
    },
    builderBrands: <any>[],
  }),
  getters: {},
  actions: {
    async getBlogList(
        model: string,
        query = {},
        sort = {},
        limit = 10,
        offset = 0
    ) {
      const {getList} = await import("../services-optimized/builderService");
      try {
        this.blogs = await getList(
            this.builder_api_key,
            model,
            query,
            sort,
            limit,
            offset
        );
        return this.blogs;
      } catch (e) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {
          model,
          query,
          sort,
          limit,
          offset,
          menuStoreError: true,
        };
  
        $customClientErrorHandler(
          e,
          "high",
          "Builder Service Error - trying to fetch blogs using getBlogList()",
          errorMeta
        );
        return e;
      }
    },

    async fetchBlogCategories() {
      try {
        const categories = await this.getBlogList("blogs");
  
        const extractUniqueCategories = () => {
          const uniqueBlogCategories = new Set();
          categories.forEach((item) => {
            if (
                item.data.categories &&
                item.data.categories.value &&
                item.data.categories.value.data
            ) {
              const categoryData = item.data.categories.value.data;
              uniqueBlogCategories.add(
                  JSON.stringify({
                    name: useTranslation(categoryData.name, categoryData.name),
                    slug: categoryData.slug,
                  })
              );
            }
          });
          return uniqueBlogCategories;
        };
  
        this.blogsCategories = Array.from(extractUniqueCategories()).map((item) =>
            JSON.parse(item)
        );
  
        const staticCategory = {
          name: useTranslation("all", "Alle"),
          slug: "all",
        };
        this.blogsCategories.unshift(staticCategory);
      } catch (error) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {
          categories: this.blogsCategories,
          menuStoreError: true,
        };
  
        $customClientErrorHandler(
          error,
          "high",
          "Builder Service Error - trying to fetch blog categories using fetchBlogCategories()",
          errorMeta
        );
        
        return error;
      }
    },

    async getBlogItem(model, query = {}, sort = {}, nuxtApp: any = null) {
      const {getItem} = await import("~/services-optimized/builderService");
      try {
        this.blogItem = await getItem(this.builder_api_key, model, query, sort,{},nuxtApp);
      } catch (e) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {
          model,
          query,
          sort,
          menuStoreError: true,
        };
  
        $customClientErrorHandler(
          e,
          "high",
          "Builder Service Error - trying to fetch blog using getBlogItem()",
          errorMeta
        );
        return e;
      }
    },

    async getNewsList(
        model: string,
        query = {},
        sort = {},
        limit: number = 100,
        offset: number = 0
    ) {
      const {getList} = await import("../services-optimized/builderService");
      try {
        // if (!this.builderObject) {
        //   this.builderObject = new BuilderService(this.builder_api_key);
        // }
        this.news = await getList(this.builder_api_key, model, query, sort, limit, offset);
        // return this.news;
      } catch (e) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {
          model,
          query,
          sort,
          limit,
          offset,
          menuStoreError: true,
        };
  
        $customClientErrorHandler(
          e,
          "high",
          "Builder Service Error - trying to fetch news using getNewsList()",
          errorMeta
        );
        return e;
      }
    },

    async getNewsItem(model, query = {}, sort = {}, nuxtApp: any = null) {
      const {getItem} = await import("~/services-optimized/builderService");
      try {
        // if (!this.builderObject) {
        //   this.builderObject = new BuilderService(this.builder_api_key);
        // }
        this.news = await getItem(this.builder_api_key,model, query, sort,{},nuxtApp);
      } catch (e) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {
          model,
          query,
          sort,
          menuStoreError: true,
        };
  
        $customClientErrorHandler(
          e,
          "high",
          "Builder Service Error - trying to fetch news using getNewsItem()",
          errorMeta
        );
        return e;
      }
    },

    async getAdviceList(
        query = {},
        sort = {},
        limit: number = 100,
        offset: number = 0
    ) {
      const {getList} = await import("../services-optimized/builderService");
      try {
        let model = "advices";
        // if (!this.builderObject) {
        //   this.builderObject = new BuilderService(this.builder_api_key);
        // }
        this.advices = await getList(this.builder_api_key,model, query, sort, limit, offset);

      } catch (e) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {
          query,
          sort,
          limit,
          offset,
          menuStoreError: true,
        };
  
        $customClientErrorHandler(
          e,
          "high",
          "Builder Service Error - trying to fetch advices using getAdviceList()",
          errorMeta
        );
        return e;
      }
    },

    async getAdviceItem(model, query = {}, sort = {}, nuxtApp: any = null) {
      const {getItem} = await import("~/services-optimized/builderService");
      try {
        // if (!this.builderObject) {
        //   this.builderObject = new BuilderService(this.builder_api_key);
        // }
        this.adviceItem = await getItem(this.builder_api_key, model, query, sort,{},nuxtApp);
      } catch (e) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {
          model,
          query,
          sort,
          menuStoreError: true,
        };
  
        $customClientErrorHandler(
          e,
          "high",
          "Builder Service Error - trying to fetch advices using getAdviceItem()",
          errorMeta
        );
        return e;
      }
    },

    async fetchAllBrands(
        query = {},
        sort = { createdDate: 1 },
        limit: number = 100,
        offset: number = 0
    ) {
      const {getList} = await import("../services-optimized/builderService");
      useStateModifier(this.brands_state, "loading");
      try {
        // const builderObject = new BuilderService(this.builder_api_key);
        this.brands = await getList(this.builder_api_key,'brands', query, sort, limit, offset) as any[];
        if (this.brands.length <= 0) {
          throw new Error("Couldn't load brands, try again later");

        } else {
          useStateModifier(this.brands_state, "success");
        }
      } catch (err) {
        const nuxtApp = tryUseNuxtApp();
        if(nuxtApp){
        const { $customClientErrorHandler } = nuxtApp;
        let errorMeta = { brandDataLayerError: true };

            $customClientErrorHandler(
          err,
          "high",
          "Builder Service Error - trying to fetch all brands using fetchAllBrands()",
          errorMeta
          );

        useStateModifier(this.brands_state, "failed");
        return err; 
      }
      }
    },

    async fetchTopBrands() {
      try {
        if (this.brands.length <= 0 && this.brands_state.status !== "loading") {
          await this.fetchAllBrands();
        }

        this.topBrands = this.brands
            .map(({ data }) => {
              return {
                ...data?.topBrand,
                title: data.title,
                redirectToBrandListing: data?.topBrand.redirectToBrandListing,
              };
            })
            ?.filter((item) => item?.isTop);
      } catch (e) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {
          brands: this.brands,
          menuStoreError: true,
        };
  
        $customClientErrorHandler(
          e,
          "high",
          "Builder Service Error - trying to fetch top brands using fetchTopBrands()",
          errorMeta
        );
        return e;
      }
    },

    async fetchBrandBySlug(slug) {
      const {getItem} = await import("~/services-optimized/builderService");
      try {

        let userAttributes = {
          urlPath: slug,
        };

        return await getItem(this.builder_api_key,"brands", userAttributes);
      } catch (e) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {
          slug,
          brands: this.builderBrands,
          menuStoreError: true,
        };
  
        $customClientErrorHandler(
          e,
          "high",
          "Builder Service Error - trying to fetch brand by slug using fetchBrandBySlug()",
          errorMeta
        );
        return e;
      }
    },

    async getItem(model = "pages", userAttributes = {}, query = {},options = {}) {
      let apiKey = this.builder_api_key;
      try {
        const response = await fetchOneEntry({
          model,
          apiKey,
          userAttributes,
          query,
          options
        });
        this.item = response;
        return response;
      } catch (error) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {
          model,
          query,
          userAttributes,
          options,
          menuStoreError: true,
        };
  
        $customClientErrorHandler(
          error,
          "high",
          "Builder Service Error - trying to fetch item using getItem()",
          errorMeta
        );
        return error;
      }
    },
    async getList(
        model = "pages",
        query = {},
        sort = {},
        limit = 100,
        offset = 0,
        nuxtApp: any = null,
        redisCacheBurst = false
    ) {
      const {getList} = await import("../services-optimized/builderService");
      this.list = [];
      try {
        const cmsResponse = await getList(
           this.builder_api_key,
            model,
            query,
            sort,
            limit,
            offset,
            nuxtApp,
            redisCacheBurst
        );
        this.list = cmsResponse;
        return cmsResponse;
      } catch (error) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {
          model,
          query,
          sort,
          limit,
          offset,
          menuStoreError: true,
        };
  
        $customClientErrorHandler(
          error,
          "high",
          "Builder Service Error - trying to fetch list using getList()",
          errorMeta
        );
        return [];
      } finally {
        this.createMonthArray();
      }
    },

    // -------------------------Events functions-------------------------------
    /**
     * Function for updating url query string as per requirements
     * @param filter
     * @param route
     * @param router
     */
    async updateUrlString(filter: string, route, router) {
      // Assigning query state to a local variable
      const query = { ...route.query };

      // Resetting relevant query parameters based on filter type
      if (filter === "past" || filter === "upcoming") {
        delete query.past;
        delete query.upcoming;
        query[filter] = true;
      } else if (filter === "Online" || filter === "Offline") {
        query.mode = filter.toLowerCase();
      } else {
        query.q = filter;
      }

      // Updating URL String
      await router.push({ path: route.path, query });
    },

    /**
     * Fetching past or upcoming events
     * @param route
     * @param mode
     */
    async fetchEvents(query = {}, sort = {}) {
      return await this.getList("events", query, sort);
    },
    handleMonthSelect(e) {
      if (e === "All") {
        this.filteredEvents = this.list;
        return;
      }
      this.filteredEvents = this.list.filter((item) => {
        const date = new Date(item.data.dateAndTime);
        const month = date.toLocaleString("default", { month: "short" });
        return month === e;
      });
    },
    createMonthArray() {
      if (!Array.isArray(this.list) || this.list.length === 0) return;
      let monthSet = new Set<string>();
      this.list.forEach((item) => {
        const date = new Date(item.data.dateAndTime);
        const month = date.toLocaleString("default", { month: "short" });
        monthSet.add(month);
      });
      this.month_array = ["All", ...Array.from(monthSet)];
    },
    handleInterested(customer_id: number) {
      if (!customer_id) {
        navigateTo("/login");
        return;
      }
      const interested_customers = this.item?.data?.interestedCustomers;

      if (typeof interested_customers === "undefined") {
        this.item.data.interestedCustomers = [];
      }

      try {
        if (interested_customers?.includes(customer_id)) {
          this.item.data.interestedCount--;
          this.item.data.interestedCustomers =
              this.item?.data?.interestedCustomers.filter(
                  (id) => id !== customer_id
              );
        } else {
          this.item.data.interestedCount++;
          this.item?.data?.interestedCustomers.push(customer_id);
        }

        this.updateInterestedCount();
      } catch (e) {
        this.is_interested = !this.is_interested;
      }
    },
    async updateInterestedCount() {
      try {
        let myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Authorization", `Bearer ${this.builder_api_pvt_key}`);
        //@ts-ignore
        const response = await $fetch(
            `https://builder.io/api/v1/write/events/${this.item.id}`,
            {
              method: "PUT",
              headers: myHeaders,
              body: JSON.stringify(this.item),
            }
        );

        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }
      } catch (error) {}
    },
    /**
     * Function for adding infix in url
     * @param url
     * @param infix
     */
    addInfixToUrl(url: string, infix: string) {
      let urlArray = url.split("/");
      urlArray.splice(2, 0, infix);
      return urlArray.join("/");
    },
    /**
     * Function for removing added infix in url
     * @param url
     * @param infix
     */
    removeInfixFromUrl(url: string, infix: string) {
      let urlArray = url.split("/");
      const infixIndex = urlArray.indexOf(infix);
      if (infixIndex !== -1) {
        urlArray.splice(infixIndex, 1);
      }
      return urlArray.join("/");
    },
    // -------------------Events End------------------------

    //---------------------Blogs, New & Advice Begin-----------------------------------

    async routeUrl(filter: string, route, router) {
      // Assigning query state to a local variable
      const query = { ...route.query };

      query.q = filter;

      // Updating URL String
      await router.push({ path: route.path, query });
    },

    //-----------------------Redirect to blogs , news and advice ------------------------

    async redirectToBlogs() {
      const router = useRouter();
      router.push({ path: "/blogs" });
    },

    async redirectToAdvices() {
      const router = useRouter();
      router.push({ path: "/advices" });
    },

    async redirectToNews() {
      const router = useRouter();
      router.push({ path: "/news" });
    },

    //--------------------------Blogs-----------------------
    async redirectToBlogsWithCategory(category) {
      const router = useRouter();
      router.push({ path: "/blogs/search", query: { category } });
    },
    async redirectToBlogsWithTags(tag) {
      const router = useRouter();
      router.push({ path: "/blogs/search", query: { tag } });
    },
    async redirectToBlogsWithInputQuery(q) {
      const router = useRouter();
      router.push({ path: "/blogs/search", query: { q } });
    },
    async redirectToBlogsWithAuthor(author) {
      const router = useRouter();
      router.push({ path: `/blogs/author/${author}` });
    },
    //------------------------Advices-------------------------
    async redirectToAdvicesWithTags(tag) {
      const router = useRouter();
      router.push({ path: "/advices/search", query: { tag } });
    },
    async redirectToAdvicesWithInputQuery(q) {
      const router = useRouter();
      router.push({ path: "/advices/search", query: { q } });
    },
    async redirectToAdvicesWithCategory(category) {
      const router = useRouter();
      router.push({ path: "/advices/search", query: { category } });
    },
    async redirectToAdvicesWithAuthor(author) {
      const router = useRouter();
      router.push({ path: `/advices/author/${author}` });
    },
    //------------------------News-------------------------------
    async redirectToNewsWithTags(tag) {
      const router = useRouter();
      router.push({ path: "/news/search", query: { tag } });
    },
    async redirectToNewsWithCategory(category) {
      const router = useRouter();
      router.push({ path: "/news/search", query: { category } });
    },
    async redirectToNewsWithInputQuery(q) {
      const router = useRouter();
      router.push({ path: "/news/search", query: { q } });
    },
    async redirectToNewsWithAuthor(author) {
      const router = useRouter();
      router.push({ path: `/news/author/${author}` });
    },
    //----------------------BlogType-------------------------------
    async redirectToBlogType(item) {
      const router = useRouter();
      const routes = {
        news: "/news",
        advices: "/advices",
        blogs: "/blogs",
      };

      const path = routes[item.slug];
      if (path) {
        router.push({ path });
      }
    },

    async fetchBrandList() {
      useStateModifier(this.brand_list_state, "loading");
    
      try {
        let brands = [];
        let offset = 0;
    
        do {
          brands = await this.getList("brands-list", {}, {}, 100, offset);
          this.builderBrands.push(...brands);
          offset += 100;
        } while (brands.length === 100);
    
        if (this.builderBrands.length <= 0) {
          throw new Error("No brands found.");
        }
    
        const orderedBrands = this.builderBrands
          .filter((item: any) => item.data.brand.isTop && item.data.brand.order !== undefined)
          .sort((a: any, b: any) => a.data.brand.order - b.data.brand.order);
    
        const unorderedBrands = this.builderBrands
          .filter((item: any) => item.data.brand.isTop && item.data.brand.order === undefined);
    
        this.builderBrands = [...orderedBrands, ...unorderedBrands].map((item: any) => ({
          brand: {
            id: item.data.brand.id,
            image: item.data.brand.image,
            color: "white",
            tag: "popular",
            alt: item.data.brand.title,
            title: item.data.brand.title,
            isTop: item.data.brand.isTop,
            order: item.data.brand.order,
            isContent: item.data.brand?.isContent,
          },
        }));
    
        useStateModifier(this.brand_list_state, "success");
      } catch (error) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {
          brands: this.builderBrands,
          menuStoreError: true,
        };
    
        $customClientErrorHandler(
          error,
          "high",
          "Builder Service Error - trying to fetch brands using fetchBrandList()",
          errorMeta
        );
        
        return error;
      }
    }  
  },
});

// Pinia hot reload
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useBuilderStore, import.meta.hot));
}
